import React, { useState, useEffect, useRef } from 'react';

/**
 * Reusable RSVP form.
 *
 * Props:
 *   project  — project object (uses project.slug, project.survey, project.block_content.rsvp/message)
 *   blocks   — array of enabled block ids (for conditional survey/message)
 *   classes  — map of CSS class names so each host template can apply its own styling.
 *              Any omitted key uses a generic `rsvp-*` fallback.
 *   onSubmitted — optional callback(() => void), fired after a successful submit.
 */
export default function RSVPForm({ project, blocks, classes = {}, onSubmitted }) {
  const bc     = (project || {}).block_content || {};
  const rsvp   = bc.rsvp || {};
  const survey = (project || {}).survey || [];
  const HAS_SURVEY  = (blocks || []).includes('survey');
  const HAS_MESSAGE = (blocks || []).includes('message');

  const cls = {
    form:       classes.form       || 'rsvp-form',
    tabs:       classes.tabs       || 'rsvp-tabs',
    tab:        classes.tab        || 'rsvp-tab',
    tabSel:     classes.tabSel     || 'is-selected',
    field:      classes.field      || 'rsvp-field',
    label:      classes.label      || 'rsvp-label',
    input:      classes.input      || 'rsvp-input',
    submit:     classes.submit     || 'rsvp-submit',
    success:    classes.success    || 'rsvp-success',
    error:      classes.error      || 'rsvp-error',
    chip:       classes.chip       || 'rsvp-chip',
    chipSel:    classes.chipSel    || 'is-selected',
    chips:      classes.chips      || 'rsvp-chips',
  };

  /* Именное приглашение: гость пришёл по персональной ссылке
     (<slug>.amorika.love/<код>). WeddingSite кладёт его в проект, чтобы
     не прокидывать проп через все шаблоны. Имя подставляем в форму
     заранее — гостю остаётся только подтвердить. */
  const guest = (project || {}).__guest || null;

  const [attend,  setAttend]  = useState('yes');
  const [name,    setName]    = useState(guest?.name || '');
  const [answers, setAnswers] = useState({});
  const [message, setMessage] = useState('');
  const [sent,    setSent]    = useState(false);
  const [sending, setSending] = useState(false);
  const [err,     setErr]     = useState('');

  /* Гость приходит асинхронно (резолв по коду ссылки) — подставляем имя,
     когда он загрузился. Только в пустое поле: если гость уже начал
     править имя, его ввод не затираем. */
  const nameTouched = useRef(false);
  useEffect(() => {
    if (guest?.name && !nameTouched.current && !name.trim()) setName(guest.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guest?.name]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr('Введите имя'); return; }
    setErr(''); setSending(true);
    try {
      const slug = (project || {}).slug;
      if (slug) {
        const res = await fetch(`/api/guests/public/${slug}`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({
            name:           name.trim(),
            attend:         attend || 'yes',
            message,
            survey_answers: answers,
            /* Ответ по именной ссылке пишется в карточку этого гостя —
               в списке он отмечается ответившим, а не дублируется. */
            guest_code:     guest?.code || undefined,
          }),
        });
        if (!res.ok) throw new Error('send failed');
      }
      setSent(true);
      onSubmitted && onSubmitted();
    } catch {
      setErr('Не удалось отправить. Попробуйте ещё раз.');
    } finally { setSending(false); }
  };

  if (sent) return (
    <div className={cls.form}>
      <p className={cls.success}>{rsvp.successText || 'Спасибо! Ваш ответ принят ✓'}</p>
    </div>
  );

  return (
    <form className={cls.form} onSubmit={submit}>
      <div className={cls.tabs}>
        {[['yes', rsvp.yesText || 'Приду!'], ['no', rsvp.noText || 'Не смогу']].map(([v, label]) => (
          <button
            type="button"
            key={v}
            className={`${cls.tab}${attend === v ? ' ' + cls.tabSel : ''}`}
            onClick={() => setAttend(v)}>
            {label}
          </button>
        ))}
      </div>

      <div className={cls.field}>
        <label className={cls.label}>{rsvp.nameLabel || 'Ваше имя и фамилия'}</label>
        <input
          className={cls.input}
          type="text"
          placeholder={rsvp.nameLabel || 'Ваше имя и фамилия'}
          value={name}
          onChange={e => { nameTouched.current = true; setName(e.target.value); }}
          required
        />
      </div>

      {HAS_SURVEY && attend === 'yes' && (
        <div data-cb-section="survey" style={{ display: 'contents' }}>
          {survey.map(q => (
            <SurveyQ
              key={q.id}
              q={q}
              value={answers[q.id]}
              onChange={v => setAnswers(prev => ({ ...prev, [q.id]: v }))}
              classes={cls}
            />
          ))}
        </div>
      )}

      {HAS_MESSAGE && (
        <div className={cls.field}>
          <label className={cls.label}>{rsvp.msgLabel || 'Дополнительная информация'}</label>
          <input
            className={cls.input}
            type="text"
            placeholder={rsvp.msgLabel || 'Дополнительная информация'}
            value={message}
            onChange={e => setMessage(e.target.value)}
          />
        </div>
      )}

      {err && <div className={cls.error}>{err}</div>}
      <button type="submit" className={cls.submit} disabled={sending}>
        {sending ? '...' : (rsvp.btnText || 'Отправить')}
      </button>
    </form>
  );
}

function SurveyQ({ q, value, onChange, classes }) {
  if (q.type === 'text') {
    return (
      <div className={classes.field}>
        <label className={classes.label}>{q.question}</label>
        <input
          className={classes.input}
          type="text"
          placeholder={q.question}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
        />
      </div>
    );
  }
  const vals = Array.isArray(value) ? value : value ? [value] : [];
  /* 'multiple' — новый стандарт (совпадает с 10 шаблонами со своим RSVP);
     'multi' оставляем для обратной совместимости. */
  const isMulti = q.type === 'multiple' || q.type === 'multi';
  const toggle = opt => {
    if (isMulti) {
      const next = vals.includes(opt) ? vals.filter(x => x !== opt) : [...vals, opt];
      onChange(next);
    } else {
      onChange(opt);
    }
  };
  return (
    <div className={classes.field}>
      <label className={classes.label}>{q.question}</label>
      <div className={classes.chips}>
        {(q.options || []).map((opt, i) => {
          const sel = vals.includes(opt);
          return (
            <button type="button" key={i}
              className={`${classes.chip}${sel ? ' ' + classes.chipSel : ''}`}
              onClick={() => toggle(opt)}>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}
